import {
  constants,
  getPaymentListingAdditionalFees,
  getPaymentListingDisplayServiceFee,
  getPaymentListingDonationTotal,
  getPaymentListingReportChargebackProtection,
  getPaymentListingReportGross,
  getPaymentListingReportNet,
  getPaymentListingReportProcessingFee,
  getPaymentListingSalesTax,
  getPaymentPlanProrationFactor,
} from '@blocktickets/shared-utils'
import { inPersonPaymentMethods } from '../../utilities/helpers'
import { getFacilityFee } from './getFacilityFee'

const { ORDER_STATUS } = constants

const isInPersonPaymentType = (pm) =>
  pm &&
  inPersonPaymentMethods.includes(
    String(pm).trim().toUpperCase().replace(/-/g, '_')
  )

const EXCLUDED_PAYMENT_METHODS = new Set([
  'COMP',
  'TRANSFER',
  'EXCHANGE',
  'RESERVED',
])

const EXCLUDED_ORDER_STATUSES = new Set(['RELEASED', 'OPEN'])

const normalizePaymentMethod = (paymentMethod) =>
  String(paymentMethod ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')

const normalizeStatus = (status) =>
  String(status ?? '')
    .trim()
    .toUpperCase()

const isVoucherRedemptionOrder = (order) =>
  order?.voucher === true ||
  normalizePaymentMethod(order?.paymentMethod) === 'VOUCHER' ||
  normalizePaymentMethod(order?.paymentType) === 'VOUCHER'

export const isRefundedOrder = (order) => {
  const s = normalizeStatus(order?.status)
  return s === normalizeStatus(ORDER_STATUS.REFUNDED) || s === 'REFUND'
}

export const getOrderPartialRefundAmount = (order) => {
  if (isRefundedOrder(order)) return 0
  const amount = Number(order?.refundAmount ?? 0)
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

export const hasPartialRefund = (order) =>
  getOrderPartialRefundAmount(order) > 0

export const getDailySettlementRefundAmount = (order) => {
  if (isRefundedOrder(order)) return Math.abs(getSettlementNet(order))
  return getOrderPartialRefundAmount(order)
}

export const getDailySettlementStatusLabel = (order, statusLabel) => {
  if (isRefundedOrder(order)) return statusLabel
  if (hasPartialRefund(order)) {
    return `${statusLabel} (Partially Refunded)`
  }
  return statusLabel
}

const COMPLETE_ORDER_STATUSES = new Set(
  [
    ORDER_STATUS.COMPLETE,
    ORDER_STATUS.PAID,
    ORDER_STATUS.CASH,
    ORDER_STATUS.PLAN,
  ].map((st) => normalizeStatus(st))
)

export const isCompleteOrder = (order) =>
  COMPLETE_ORDER_STATUSES.has(normalizeStatus(order?.status))

/** Stripe processing fee (uses estimated fee when destination charges apply). */
export const getSettlementProcessingFee = (order) =>
  getPaymentListingReportProcessingFee(order)

/** Net to org, derived from raw order columns. */
export const getSettlementNet = (order) => getPaymentListingReportNet(order)

/** Additional/custom fees for settlement. */
export const getSettlementAdditionalFees = (order) =>
  getPaymentListingAdditionalFees(order)

/** Club Seat License fees: $339 per IceDogs club-seat season ticket. */
export const getSettlementClubSeatLicenseFees = (order) => {
  const hasCalculatedAmount =
    order?.clubSeatLicenseFees !== null &&
    order?.clubSeatLicenseFees !== undefined &&
    order?.clubSeatLicenseFees !== ''
  const amount = Number(
    hasCalculatedAmount
      ? order.clubSeatLicenseFees
      : (order?.clubSeatLicenseFee ?? 0)
  )
  if (!Number.isFinite(amount) || amount <= 0) return 0

  if (hasCalculatedAmount) return amount

  return Math.round(amount * getPaymentPlanProrationFactor(order) * 100) / 100
}

/** Custom fees other than the separately reported Club Seat License fee. */
export const getSettlementOtherAdditionalFees = (order) => {
  const calculatedAmount = Number(order?.additionalFees)
  if (
    order?.additionalFees !== null &&
    order?.additionalFees !== undefined &&
    order?.additionalFees !== '' &&
    Number.isFinite(calculatedAmount)
  ) {
    return calculatedAmount
  }

  return Math.max(
    0,
    Math.round(
      (getSettlementAdditionalFees(order) -
        getSettlementClubSeatLicenseFees(order)) *
        100
    ) / 100
  )
}

export const getSettlementChargebackProtection = (order) =>
  getPaymentListingReportChargebackProtection(order)

export const getSettlementSalesTax = (order) => getPaymentListingSalesTax(order)

export const getSettlementDonationTotal = (order) =>
  getPaymentListingDonationTotal(order)

export const getSettlementGross = (order) => getPaymentListingReportGross(order)

/** Actual service fee collected (including destination-charge orders). */
export const getSettlementServiceFee = (order) =>
  getPaymentListingDisplayServiceFee(order)

/** Same timestamp Order Listings uses for "Order date" (processed_at). */
export const getSettlementOrderTimestamp = (order) =>
  order?.processedAt ?? order?.createdAt

/**
 * In-person service fee owed. Skip zeroed-out / $0-gross orders so leftover
 * fee columns are not invoiced after seats were removed.
 */
export const getSettlementServiceFeeOwed = (order) => {
  if (isRefundedOrder(order)) return 0
  if (!isInPersonPaymentType(order?.paymentMethod)) return 0
  if (getSettlementGross(order) === 0) return 0
  return getSettlementServiceFee(order)
}

/**
 * @param {Array<Record<string, unknown>>} apiRows — rows from getPaymentListingsV4
 * @returns {{ raw: Array<Record<string, unknown>>, byPaymentMethod: Array<Record<string, unknown>> }}
 */
export function buildDailySettlementDataFromListings(apiRows) {
  const raw = (apiRows || [])
    .filter(
      (order) =>
        !EXCLUDED_PAYMENT_METHODS.has(
          normalizePaymentMethod(order?.paymentMethod)
        ) &&
        !EXCLUDED_ORDER_STATUSES.has(normalizeStatus(order?.status)) &&
        !isVoucherRedemptionOrder(order)
    )
    .map((order) => ({
      ...order,
      serviceFee: getSettlementServiceFee(order),
      totalFeeAmount: getSettlementAdditionalFees(order),
      clubSeatLicenseFees: getSettlementClubSeatLicenseFees(order),
      additionalFees: getSettlementOtherAdditionalFees(order),
      gross: getSettlementGross(order),
      serviceFeeOwed: getSettlementServiceFeeOwed(order),
    }))

  const byPaymentMethod = raw.reduce((acc, order) => {
    if (!isCompleteOrder(order)) return acc
    const key =
      String(order?.paymentMethod ?? '')
        .trim()
        .toLowerCase() || 'unknown'
    if (!acc[key]) {
      acc[key] = {
        paymentMethod: order?.paymentMethod ?? 'Unknown',
        orderCount: 0,
        ticketsSold: 0,
        netSales: 0,
        serviceFees: 0,
        facilityFees: 0,
        clubSeatLicenseFees: 0,
        additionalFees: 0,
        processingFees: 0,
        chargebackFees: 0,
        salesTax: 0,
        donationTotals: 0,
        grossSales: 0,
      }
    }
    acc[key].orderCount += 1
    acc[key].ticketsSold += Number(order?.quantity ?? 1)
    acc[key].netSales += getSettlementNet(order)
    acc[key].serviceFees += getSettlementServiceFee(order)
    acc[key].facilityFees += getFacilityFee(order)
    acc[key].clubSeatLicenseFees += getSettlementClubSeatLicenseFees(order)
    acc[key].additionalFees += getSettlementOtherAdditionalFees(order)
    acc[key].processingFees += getSettlementProcessingFee(order)
    acc[key].chargebackFees += getSettlementChargebackProtection(order)
    acc[key].salesTax += getSettlementSalesTax(order)
    acc[key].donationTotals += getSettlementDonationTotal(order)
    acc[key].grossSales += getSettlementGross(order)
    return acc
  }, {})

  return {
    raw,
    byPaymentMethod: Object.values(byPaymentMethod).sort((a, b) =>
      a.paymentMethod.localeCompare(b.paymentMethod)
    ),
  }
}
